import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, FileText, Globe, CheckSquare, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Wordmark from '../components/Wordmark';
import CircularProgress from '../components/CircularProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/preview`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Could not load your preview.');
        setLoading(false);
      });
  }, [user]);

  // Once a user is subscription-active, bounce them straight to the dashboard
  // (covers refresh after payment).
  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/billing/status`)
      .then((r) => {
        if (r.data?.subscription_status === 'active') {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => {});
  }, [user, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#1a1a1a]/60">Loading...</p>
      </div>
    );
  }
  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  const score = data?.compliance_score || 0;
  const roadmap = data?.roadmap || [];
  const previewItems = roadmap.slice(0, 2);
  const lockedItems = Math.max(roadmap.length - 2, 0);
  const policiesCount = (data?.policies || []).length || 3;
  const tasksCount = (data?.monthly_tasks || []).length || 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Wordmark />
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#0a5c44]/10 border border-[#0a5c44]/20 px-3 py-1.5 rounded-full mb-5">
            <span className="w-1.5 h-1.5 bg-[#0a5c44] rounded-full"></span>
            <span className="text-xs font-medium text-[#0a5c44]">
              Free preview
            </span>
          </div>
          <h1
            data-testid="preview-page-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-3 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Your compliance score is ready
          </h1>
          <p className="text-base sm:text-lg text-[#1a1a1a]/70 max-w-xl mx-auto">
            Here's a free preview of what we generated. Unlock the full package below.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-[#1a1a1a]/60 py-20">Loading your preview...</div>
        ) : error ? (
          <div className="text-center text-red-700 py-20" data-testid="preview-error">
            {error}
          </div>
        ) : (
          <>
            {/* Score — fully visible */}
            <div
              data-testid="preview-score-card"
              className="bg-white border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 mb-6 flex flex-col sm:flex-row items-center gap-6 sm:gap-8"
            >
              <CircularProgress value={score} size={140} strokeWidth={10} />
              <div className="text-center sm:text-left">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-[#0a5c44] mb-2">
                  Your compliance score
                </p>
                <h2
                  className="text-2xl sm:text-3xl font-normal mb-2 leading-tight"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  {scoreHeadline(score)}
                </h2>
                <p className="text-sm sm:text-base text-[#1a1a1a]/70 leading-relaxed max-w-md">
                  Your score is <strong className="text-[#1a1a1a]">{score}/100</strong>. Complete your compliance package to improve it.
                </p>
              </div>
            </div>

            {/* Roadmap — first 2 items visible, rest locked */}
            <div
              data-testid="preview-roadmap-card"
              className="bg-white border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 mb-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-xl sm:text-2xl font-normal leading-tight"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  Your compliance roadmap
                </h3>
                {lockedItems > 0 ? (
                  <span
                    data-testid="preview-roadmap-unlock-hint"
                    className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-[#0a5c44]"
                  >
                    Unlock {lockedItems} more priority action{lockedItems === 1 ? '' : 's'}
                    <span aria-hidden="true">→</span>
                  </span>
                ) : (
                  <span className="text-xs text-[#1a1a1a]/55">All items shown</span>
                )}
              </div>

              <ul className="space-y-3">
                {previewItems.map((it, i) => (
                  <li
                    key={i}
                    className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-4 sm:p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-[#1a1a1a] mb-1.5">
                          {it.title || it.task || `Roadmap item ${i + 1}`}
                        </p>
                        <p className="text-xs sm:text-sm text-[#1a1a1a]/65 leading-relaxed">
                          {it.description || it.detail || ''}
                        </p>
                      </div>
                      {it.priority && (
                        <PriorityBadge level={it.priority} />
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {lockedItems > 0 && (
                <div className="mt-4">
                  <LockedCard
                    testid="preview-roadmap-locked-card"
                    icon={<Lock className="w-5 h-5 text-[#0a5c44]" />}
                    title={`+ ${lockedItems} more priority action${lockedItems === 1 ? '' : 's'} locked`}
                  />
                </div>
              )}
            </div>

            {/* Policies — locked */}
            <LockedCard
              testid="preview-policies-card"
              icon={<FileText className="w-5 h-5 text-[#0a5c44]" />}
              title="Policy documents"
              subtitle={`${policiesCount} professional policy templates tailored to your business.`}
            />

            {/* Trust Page — locked */}
            <LockedCard
              testid="preview-trust-card"
              icon={<Globe className="w-5 h-5 text-[#0a5c44]" />}
              title="Public Trust Page"
              subtitle="A shareable security & compliance page to send to any prospect."
            />

            {/* Monthly checklist — locked */}
            <LockedCard
              testid="preview-checklist-card"
              icon={<CheckSquare className="w-5 h-5 text-[#0a5c44]" />}
              title="Monthly compliance checklist"
              subtitle={`${tasksCount || 'A'} prioritised tasks to keep your posture strong every month.`}
            />

            {/* Unlock CTA */}
            <div
              data-testid="preview-unlock-section"
              className="bg-[#0a5c44] text-white rounded-2xl p-7 sm:p-10 mt-8 text-center"
            >
              <h3
                className="text-2xl sm:text-3xl font-normal mb-3 leading-tight"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                Your compliance package is waiting.
              </h3>
              <p className="text-sm sm:text-base text-white/80 max-w-xl mx-auto mb-6">
                Get your complete roadmap, 3 policy templates, shareable Trust Page and monthly checklist.
              </p>
              <button
                type="button"
                onClick={() => navigate('/payment')}
                data-testid="preview-unlock-button"
                className="inline-flex items-center gap-2 bg-white text-[#0a5c44] hover:bg-[#f5f3ef] transition-colors h-12 px-7 rounded-md text-sm font-semibold"
              >
                Unlock for $39/month
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-white/70 mt-4">
                Cancel anytime. No contracts.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* -------------------- helpers -------------------- */

function scoreHeadline(score) {
  if (score >= 80) return 'You\'re in great shape.';
  if (score >= 60) return 'Solid foundation, room to grow.';
  if (score >= 40) return 'A few important gaps to close.';
  return 'Let\'s get you compliance-ready.';
}

const PriorityBadge = ({ level }) => {
  const styles = {
    High: 'bg-red-50 text-red-700 border-red-200',
    Medium: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] font-semibold tracking-wide uppercase border px-2 py-0.5 rounded-full shrink-0 ${
        styles[level] || styles.Medium
      }`}
    >
      {level}
    </span>
  );
};

const LockedCard = ({ icon, title, subtitle, testid }) => (
  <div
    data-testid={testid}
    className="relative bg-white border border-[#e0ddd8] rounded-2xl p-6 sm:p-7 mb-6 overflow-hidden"
  >
    <div className="flex items-start gap-3 mb-3">
      {icon}
      <div className="flex-1 min-w-0">
        <h3
          className="text-lg sm:text-xl font-normal leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          {title}
        </h3>
        {subtitle && (
          <p className="text-sm text-[#1a1a1a]/65 mt-1">{subtitle}</p>
        )}
      </div>
      <span className="inline-flex items-center gap-1.5 text-xs text-[#0a5c44] font-medium border border-[#0a5c44]/30 bg-[#0a5c44]/5 px-2.5 py-1 rounded-full">
        <Lock className="w-3 h-3" /> Locked
      </span>
    </div>

    {/* Blurred placeholder content */}
    <div className="relative mt-4 pointer-events-none select-none" aria-hidden="true">
      <div className="space-y-2.5 blur-[6px] opacity-70">
        <div className="h-3 rounded bg-[#1a1a1a]/15 w-11/12"></div>
        <div className="h-3 rounded bg-[#1a1a1a]/15 w-10/12"></div>
        <div className="h-3 rounded bg-[#1a1a1a]/15 w-9/12"></div>
      </div>
    </div>
  </div>
);

export default PreviewPage;
